import GenericTable from "@/components/GenericTable";
import GenericTableElement from "@/components/GenericTableElement";
import { Tooltip } from "@/components/ui/tooltip";
import { now } from "@/helpers/time";
import {
  Badge,
  Checkbox,
  HStack,
  IconButton,
  Spinner,
  Table as ChakraTable,
} from "@chakra-ui/react";
import { Download } from "lucide-react";
import { formatDocument } from "../../screenings/columns";
import { canChange, canDownloadCertificate, isExpired } from "../changes";
import {
  CONTRACT_TABLE_COLUMNS,
  activeCoverages,
  coverageLabels,
  formatCalendarDate,
} from "../columns";
import { Contract } from "../types";
import { useDownloadCertificate } from "../useDownloadCertificate";
import PendingChangeBadges from "./PendingChangeBadges";
import StatusBadge from "./StatusBadge";

type Props = {
  isLoading: boolean;
  contracts: Contract[];
  onSelect: (contract: Contract) => void;
  selectable: boolean;
  selectedIds: Set<string>;
  onToggle: (contractId: string, selected: boolean) => void;
  onToggleAll: (selected: boolean) => void;
};

const statusTooltip = (contract: Contract) => {
  if (contract.status === "preEnrolled")
    return `Ingresa a la fianza el ${formatCalendarDate(contract.enrolledAt)}`;
  if (contract.status === "retired" && contract.retiredAt)
    return `Fuera de la fianza desde el ${formatCalendarDate(
      contract.retiredAt
    )}`;
  return undefined;
};

const Table = ({
  isLoading,
  contracts,
  onSelect,
  selectable,
  selectedIds,
  onToggle,
  onToggleAll,
}: Props): JSX.Element => {
  const { downloadCertificate, downloadingIds } = useDownloadCertificate();
  const today = now();
  const changeable = contracts.filter(canChange);
  const allSelected =
    changeable.length > 0 &&
    changeable.every((contract) => selectedIds.has(contract.id));

  const columns = selectable
    ? [
        {
          key: "select",
          label: "",
          header: (
            <Checkbox.Root
              aria-label="Seleccionar todos"
              checked={allSelected}
              disabled={changeable.length === 0}
              onCheckedChange={(e) => onToggleAll(!!e.checked)}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          ),
        },
        ...CONTRACT_TABLE_COLUMNS,
      ]
    : CONTRACT_TABLE_COLUMNS;

  return (
    <GenericTable isLoading={isLoading} columns={columns} interactive size="sm">
      {contracts.map((contract) => (
        <ChakraTable.Row
          key={contract.id}
          cursor="pointer"
          onClick={() => onSelect(contract)}
        >
          {selectable && (
            <ChakraTable.Cell
              borderColor="#e5e7eb"
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox.Root
                aria-label={`Seleccionar ${contract.contractNumber}`}
                checked={selectedIds.has(contract.id)}
                disabled={!canChange(contract)}
                onCheckedChange={(e) => onToggle(contract.id, !!e.checked)}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            </ChakraTable.Cell>
          )}
          <GenericTableElement value={contract.contractNumber} />
          <GenericTableElement value={formatDocument(contract)} />
          <GenericTableElement value={contract.tenantName} />
          <GenericTableElement value={contract.propertyAddress} />
          <GenericTableElement value={contract.monthlyRent} formatNumber />
          <GenericTableElement
            value={formatCalendarDate(contract.enrolledAt)}
          />
          <GenericTableElement
            value={formatCalendarDate(contract.rentalsContractEndDate)}
            suffix={
              canChange(contract) && isExpired(contract, today) ? (
                <Badge bg="#FFF7D6" color="#E6AE22" size="sm">
                  vencido
                </Badge>
              ) : undefined
            }
          />
          <GenericTableElement
            value={contract.id}
            actions={
              <HStack gap={1}>
                {activeCoverages(contract).map((key) => (
                  <Tooltip key={key} content={coverageLabels[key].label}>
                    <Badge bg="#EDEBFF" color="#7B61FA" size="sm">
                      {coverageLabels[key].short}
                    </Badge>
                  </Tooltip>
                ))}
              </HStack>
            }
          />
          <GenericTableElement
            value={contract.status}
            tooltip={statusTooltip(contract)}
            actions={
              <HStack gap={1} wrap="wrap">
                <StatusBadge status={contract.status} />
                <PendingChangeBadges contract={contract} />
              </HStack>
            }
          />
          <GenericTableElement
            value={undefined}
            actions={
              canDownloadCertificate(contract) && (
                <IconButton
                  aria-label="Descargar certificado"
                  variant="ghost"
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadCertificate(contract.id);
                  }}
                  disabled={downloadingIds.has(contract.id)}
                >
                  {downloadingIds.has(contract.id) ? (
                    <Spinner size="sm" />
                  ) : (
                    <Download size="1.2rem" />
                  )}
                </IconButton>
              )
            }
          />
        </ChakraTable.Row>
      ))}
    </GenericTable>
  );
};

export default Table;
